// @ts-nocheck
import * as cssLinter from "css";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridWidths } from "@insite/mobius/GridItem";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import React, { FC } from "react";
import { css } from "styled-components";

const enum SideColumnSide {
    Left = "Left",
    Right = "Right",
}

const enum fields {
    side = "side",
    spacing = "spacing",
    hideForMobile = "hideForMobile",
    blockPreserveSpacing = "blockPreserveSpacing",
    columnSize = "columnSize",
    customCSS = "customCSS",
}

const columnSizeOptions: Option<number>[] = [
    { displayName: "Tiny", value: 1 },
    { displayName: "Extra Small", value: 2 },
    { displayName: "Small", value: 3 },
    { displayName: "Medium", value: 4 },
    { displayName: "Large", value: 5 },
    { displayName: "Extra Large", value: 6 },
];

const defaultCustomCss = `& {
}

.container {
}

.left-column {
}

.right-column {
}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

interface Props extends WidgetProps {
    fields: {
        [fields.side]: string;
        [fields.hideForMobile]: boolean;
        [fields.columnSize]: GridWidths;
        [fields.spacing]: string;
        [fields.customCSS]: string;
    };
}

const getColumnWidth = (columnSize: GridWidths, gap: string) => {
    const percentage = (columnSize / 12) * 100;
    if (!gap) {
        return `${percentage}%`;
    }
    const result = `calc(${percentage}% - (${gap} / 2))`;
    return result;
};

const SideColumnRow: FC<Props> = ({ fields: { customCSS, ...otherFields }, id }) => {
    const { side, hideForMobile, spacing } = otherFields;

    const sideColumnSize = otherFields.columnSize;
    const mainColumnSize = (12 - sideColumnSize) as GridWidths;

    const mainColumnStyles = {
        css: css`
            display: flex;
            ${breakpointValues(["100%", "100%", getColumnWidth(mainColumnSize, spacing)], "width")}
        `,
    };
    const sideColumnStyles = {
        css: css`
            ${breakpointValues(["100%", "100%", getColumnWidth(sideColumnSize, spacing)], "width")}

            ${breakpointValues([hideForMobile ? "none" : "flex", hideForMobile ? "none" : "flex", "flex"], "display")}
        `,
    };

    const leftColumnStyles = side === SideColumnSide.Left ? sideColumnStyles : mainColumnStyles;
    const rightColumnStyles = side === SideColumnSide.Right ? sideColumnStyles : mainColumnStyles;

    const containerStyles = {
        css: css`
            gap: ${spacing};
            width: 100%;
            display: flex;
            ${breakpointValues(["wrap", "wrap", "nowrap"], "flex-wrap")}
        `,
    };

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...containerStyles} className="container">
                <StyledWrapper {...leftColumnStyles} className="left-column">
                    <Zone contentId={id} zoneName="Content0" />
                </StyledWrapper>
                <StyledWrapper {...rightColumnStyles} className="right-column">
                    <Zone contentId={id} zoneName="Content1" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const gapField = getCssUnitField(fields.spacing);

const widgetModule: WidgetModule = {
    component: SideColumnRow,
    definition: {
        group: "Basic",
        icon: "Columns",
        fieldDefinitions: [
            {
                name: fields.side,
                displayName: "Side Column",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Left",
                        value: SideColumnSide.Left,
                    },
                    {
                        displayName: "Right",
                        value: SideColumnSide.Right,
                    },
                ],
                defaultValue: SideColumnSide.Left,
                fieldType: "General",
                sortOrder: 0,
                isRequired: true,
            },
            {
                name: fields.hideForMobile,
                displayName: "Hide side column for smaller devices",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                ...gapField.definition,
                fieldType: "Contextual",
            },

            {
                name: fields.columnSize,
                displayName: "Side Column Size",
                editorTemplate: "DropDownField",
                options: columnSizeOptions,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (!value) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
